import React, { useEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { UserOptionType } from 'src/api/types';
import { GTCInlineWrapper } from 'src/components/GenericTable';
import UserInput from 'src/components/UserInput/UserInput';
import { useUpsertControlMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import styled from 'styled-components';
import _ from 'lodash';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }

  .MuiFormControl-root {
    width: 100%;
    height: 100%;
    margin: 0;
  }
  .MuiInputBase-root {
    width: 100%;
    height: 100%;
    font: inherit;
    background: transparent !important;

    &::before,
    &::after {
      display: none;
    }
  }
  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
  .MuiInputBase-input {
    height: 100%;
    padding: 0 14px;
    font: inherit;
    box-sizing: border-box;
  }
`;

interface OwnerCellProps {
  controlId: string;
  value: UserOptionType | null | undefined;
}

export const OwnerCell: React.FC<OwnerCellProps> = ({ controlId, value }) => {
  const [upsertControl, { loading }] = useUpsertControlMutation();
  const { workspaceId } = useAuth();
  const [draft, setDraft] = useState<UserOptionType>(value || { text: '' });
  const draftRef = useRef(draft);

  useEffect(() => {
    const next = value || { text: '' };
    setDraft(next);
    draftRef.current = next;
  }, [value]);

  const isSameOwner = (a: UserOptionType | null | undefined, b: UserOptionType | null | undefined) => {
    if (!a?.text && !a?.email && !b?.text && !b?.email) return true;
    if (a?.memberId && b?.memberId) return a.memberId === b.memberId;
    if (a?.email && b?.email) return a.email === b.email && a.text === b.text;
    return a?.text === b?.text && !a?.email && !b?.email;
  };

  const save = (owner: UserOptionType) => {
    if (isSameOwner(owner, value)) return;
    const { __typename, ...cleanOwner } = owner || {};
    upsertControl({
      variables: {
        controlData: { id: controlId, owner: cleanOwner },
        workspaceId,
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Control', id: controlId }),
          fields: {
            owner: () => owner,
          },
        });
      },
      onCompleted: () => queryCache.invalidateQueries([QKeys.Audit]),
    });
  };

  return (
    <GTCInlineWrapper>
      <Wrapper>
        <UserInput
          value={draft}
          onChange={(usr) => {
            setDraft(usr);
            draftRef.current = usr;
          }}
          onBlur={() => save(draftRef.current)}
          onSelectUser={(usr) => save(usr)}
          placeholder="Select owner"
          disabled={loading}
        />
      </Wrapper>
    </GTCInlineWrapper>
  );
};
